import {_} from 'vue-underscore';

export default class ColorService {

    /**
     * Generate random colour in hex code
     * @returns {string}
     */
    randomHex() {
        return '#'+(Math.random() * 0xFFFFFF << 0).toString(16).padStart(6, '0');
    }


    /**
     * Generate shades for the color specified with number of shades
     *
     * @param colour
     * @param shadesStep
     * @returns {*[]}
     */
    getShades(colourHex, shadesStep) {

        const whiteHex = '#ffffff';
        const blackHex = '#000000';

        let lighter = [];
        let darker = [];
        _.times(50, function(n){
            if(n % shadesStep === 0) {
                let c = 50 - n;
                let ln = this.lighten(colourHex, c);
                if(ln !== whiteHex) {
                    lighter.push(ln);
                }
            }
        }, this);


        let cb = this.brightness(colourHex);
        _.times(50, function(n){
            if(n % shadesStep === 0) {
                let dn = this.darken(colourHex, n);
                if(dn !== blackHex) {
                    if(cb < shadesStep) {
                        darker.push(dn);
                    } else {
                        if(cb - this.brightness(dn) > shadesStep) {
                            darker.push(dn);
                        }
                    }
                }
            }
        }, this);

        // overriding the last part
        lighter = _.unique(lighter);
        darker = _.unique(darker);

        _.each(darker, function(darkColour, index) {
            if(darker.length == index + 1) {
                darker[index] = darker[index-1];
            }
        });

        return lighter.concat([colourHex]).concat(darker);
    }

    lighten(colourHex, percent) {
        let hsl = this.toHSL(colourHex);
        let h = hsl[0],
            s = hsl[1],
            l = Math.min(100, hsl[2] + percent);

        return this.hslToHex(h, s, l);
    };

    darken(colourHex, percent) {
        let hsl = this.toHSL(colourHex);
        let h = hsl[0],
            s = hsl[1],
            l = Math.max(0, hsl[2] - percent);

        return this.hslToHex(h, s, l);
    };

    toRGB(colourHex) {
        let colorObj = this._cache(colourHex);
        if (colorObj.rgb) {
            return colorObj.rgb;
        }
        let h = this.toHex(colourHex);

        colorObj.rgb = [
            parseInt(h.substr(1, 2), 16),
            parseInt(h.substr(3, 2), 16),
            parseInt(h.substr(5, 2), 16)
        ];

        return colorObj.rgb;
    };

    toHex(colourHex) {
        let colorObj = this._cache(colourHex);
        if (colorObj.hex) {
            return colorObj.hex;
        }

        if (colourHex.substr(0, 1) === '#' && colourHex.length === 7) {
            colorObj.hex = '' + colourHex;
        } else if (colourHex.substr(0, 1) === '#' && colourHex.length === 4) {
            colorObj.hex = '#' +
                colourHex.substr(1, 1) + colourHex.substr(1, 1) +
                colourHex.substr(2, 1) + colourHex.substr(2, 1) +
                colourHex.substr(3, 1) + colourHex.substr(3, 1);
        }

        return colorObj.hex;
    }

    toHSL(colourHex) {
        let r = this.toRGB(colourHex)[0] / 255, // red
            g = this.toRGB(colourHex)[1] / 255, // green
            b = this.toRGB(colourHex)[2] / 255; //blue

        let max = Math.max(r, g, b),
            min = Math.min(r, g, b);

        let d = max - min; // Delta RGB value

        let h,
            s,
            l = (max + min) / 2;

        if (d === 0) {
            // gray?, no chroma...
            h = 0;
            s = 0;
        } else {
            // Chromatic data...
            s = d / ( l < 0.5 ? ( max + min ) : ( 2 - max - min ));

            let delR = ( ( ( max - r ) / 6 ) + ( d / 2 ) ) / d;
            let delG = ( ( ( max - g ) / 6 ) + ( d / 2 ) ) / d;
            let delB = ( ( ( max - b ) / 6 ) + ( d / 2 ) ) / d;

            if (r === max) {
                h = delB - delG;
            } else if (g === max) {
                h = ( 1 / 3 ) + delR - delB;
            } else if (b === max) {
                h = ( 2 / 3 ) + delG - delR;
            }

            if (h < 0) {
                h += 1;
            }
            if (h > 0) {
                h -= 1;
            }
        }

        h = Math.round(h * 360);
        if (h < 0) {
            h += 360;
        }

        let colorObj = this._cache(colourHex); //@todo why?

        colorObj.hsl = [h, Math.round(s * 100), Math.round(l * 100)];

        return colorObj.hsl;
    };

    hslToHex(h, s, l) {
        if (Array.isArray(h)) {
            h = h[0] || 0;
            l = h[2] || 0;
            s = h[1] || 0;
        }

        // HSL from 0 to 1
        h = ((h + 360) % 360.0) / 360;
        s = s / 100.0;
        l = l / 100.0;

        let rgb = this.hslToRGB(h, s, l);
        return '#' + this._hex2(rgb[0]) + this._hex2(rgb[1]) + this._hex2(rgb[2]);
    };

    hslToRGB(h, s, l) {
        // HSL 0 to 1; RGB results from 0 to 255
        let r,g,b;

        if (s === 0) {
            r = l * 255;
            g = l * 255;
            b = l * 255;
        } else {
            let v2 = (l < 0.5) ? l * ( 1 + s ) : (( l + s ) - ( s * l ));
            let v1 = 2 * l - v2;

            r = 255 * this._h2rgb(v1, v2, h + ( 1 / 3 ));
            g = 255 * this._h2rgb(v1, v2, h);
            b = 255 * this._h2rgb(v1, v2, h - ( 1 / 3 ));
        }
        return [r,g,b];
    };

    _h2rgb(v1, v2, vH) {
        if (vH < 0) {
            vH += 1;
        }
        if (vH > 1) {
            vH -= 1;
        }
        if (( 6 * vH ) < 1) {
            return ( v1 + ( v2 - v1 ) * 6 * vH );
        }
        if (( 2 * vH ) < 1) {
            return ( v2 );
        }
        if (( 3 * vH ) < 2) {
            return ( v1 + ( v2 - v1 ) * ( ( 2 / 3 ) - vH ) * 6 );
        }

        return ( v1 );
    }

    _hex2(n) {
        let h = Math.round(n).toString(16);
        if (h.length === 1) {
            h = '0' + h;
        }
        return h.substr(0, 1) + h.substr(1, 1);
    }



    // returns (or creates) the cached color structure
    _cache(colourHex) {

        if(typeof _immutableCache === 'undefined') {
            var _immutableCache = {};
        }
        if (typeof _immutableCache[colourHex] === 'undefined' || ! _immutableCache[colourHex]) {
            _immutableCache[colourHex] = {};
        }
        return _immutableCache[colourHex];
    }

    brightness(colourHex) {
        let r, g, b;

        if (colourHex.match(/^rgb/)) {
            colourHex = colourHex.match(/rgb\(([^)]+)\)/)[1];
            colourHex = colourHex.split(/ *, */).map(Number);
            r = colourHex[0];
            g = colourHex[1];
            b = colourHex[2];
        } else if ('#' === colourHex[0] && 7 === colourHex.length) {
            r = parseInt(colourHex.slice(1, 3), 16);
            g = parseInt(colourHex.slice(3, 5), 16);
            b = parseInt(colourHex.slice(5, 7), 16);
        } else if ('#' === colourHex[0] && 4 === colourHex.length) {
            r = parseInt(colourHex[1] + colourHex[1], 16);
            g = parseInt(colourHex[2] + colourHex[2], 16);
            b = parseInt(colourHex[3] + colourHex[3], 16);
        }

        return (r * 299 + g * 587 + b * 114) / 1000;
    }
}
